import { createHash, randomUUID } from "node:crypto";
import { request } from "undici";
import { z } from "zod";
import type { AuthProvider } from "../auth/types.js";
import { ApiError } from "../utils/errors.js";

const GUID =
  /^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$(?![\s\S])/;
const HASH = /^[a-fA-F0-9]{64}$(?![\s\S])/;
const ORIGIN =
  /^https:\/\/[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.(?:api\.)?crm[0-9]{0,2}\.dynamics\.com$(?![\s\S])/i;
const FILENAME = /^[A-Za-z0-9][A-Za-z0-9._ -]{0,95}\.(?:txt|md)$(?![\s\S])/;
const MAX_BYTES = 1024 * 1024;
const DEADLINE_MS = 30_000;
const ROW_SELECT =
  "botcomponentid,_parentbotid_value,componenttype,schemaname,name,description,filedata";
const sha = (value: string | Buffer) => createHash("sha256").update(value).digest("hex");
const object = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);
const guid = (value: unknown): value is string => typeof value === "string" && GUID.test(value);

export const createCopilotKnowledgeTextFileInputSchema = z
  .object({
    agentId: z.string().regex(GUID),
    filename: z.string().regex(FILENAME),
    content: z.string().min(1).max(MAX_BYTES),
    description: z.string().max(1000).default(""),
    confirm: z.boolean().default(false),
    plannedComponentId: z.string().regex(GUID).optional(),
    expectedPlanSha256: z.string().regex(HASH).optional(),
  })
  .strict()
  .superRefine((value, context) => {
    if (
      value.confirm !==
        (value.plannedComponentId !== undefined && value.expectedPlanSha256 !== undefined) ||
      (!value.confirm &&
        (value.plannedComponentId !== undefined || value.expectedPlanSha256 !== undefined))
    ) {
      context.addIssue({
        code: "custom",
        message:
          "Preview first; confirmation requires both returned plan fields, and preview accepts neither.",
      });
    }
    // Zod may run refinements after a max-length issue; do not allocate an
    // additional unbounded encoding buffer for an already oversized input.
    if (value.content.length > MAX_BYTES) return;
    const bytes = Buffer.from(value.content, "utf8");
    if (bytes.length > MAX_BYTES || bytes.toString("utf8") !== value.content) {
      context.addIssue({
        code: "custom",
        path: ["content"],
        message: "Content must be well-formed UTF-8 text within 1 MiB.",
      });
    }
  });

export class CopilotKnowledgeFileError extends ApiError {
  constructor(
    readonly reason:
      | "input"
      | "configuration"
      | "authentication"
      | "identity"
      | "preflight"
      | "response"
      | "deadline"
      | "http",
    status?: number,
  ) {
    super(
      `Copilot knowledge-file storage ${reason} check failed${status === undefined ? "" : ` (HTTP ${status})`}. No automatic retry, sign-in, fallback, deletion or publication was attempted.`,
      status ?? 502,
    );
    this.name = "CopilotKnowledgeFileError";
  }
}

const LIMITATION =
  "EXPERIMENTAL storage only: this does not prove knowledge ingestion, search indexing, agent use, publication or answer quality. Uploaded content can become available to agent users. File-column If-Match enforcement has not been service-verified; only this call's newly created row is eligible for upload. No replacement, retry, rollback or deletion is performed.";

/**
 * Metadata concepts: Microsoft's FileAttachmentComponent has schema/display name,
 * description and ParentBotId, but no knowledge-source YAML/configuration:
 * https://github.com/microsoft/vscode-copilotstudio/blob/f9a34b95cef3611cdfc589b31dd32d7b8cce8635/src/CopilotStudio.Sync/WorkspaceSynchronizer.cs#L2025
 * The public extension saves metadata through ContentAuthoringService; direct
 * Dataverse type-14 creation here is experimental, NOT ingestion-equivalence proof.
 * Binary protocol: https://learn.microsoft.com/en-us/power-apps/developer/data-platform/file-column-data#upload-a-file-in-a-single-request-by-using-web-api
 */
export class CopilotKnowledgeFileApi {
  private readonly origin: string;
  constructor(
    private readonly config: {
      authProvider: Pick<AuthProvider, "getCurrentAccount" | "tryGetTokenForResourceSilent">;
      environmentUrl: string;
    },
  ) {
    if (typeof config.environmentUrl !== "string" || !ORIGIN.test(config.environmentUrl))
      throw new CopilotKnowledgeFileError("configuration");
    this.origin = config.environmentUrl.toLowerCase();
  }

  async createTextFile(input: unknown) {
    const parsed = createCopilotKnowledgeTextFileInputSchema.safeParse(input);
    if (!parsed.success) throw new CopilotKnowledgeFileError("input");
    const args = parsed.data;
    const agentId = args.agentId.toLowerCase();
    const content = Buffer.from(args.content, "utf8");
    const contentSha256 = sha(content);
    const plannedComponentId = args.plannedComponentId?.toLowerCase() ?? randomUUID();
    const auth = this.config.authProvider;
    let before;
    try {
      before = auth.getCurrentAccount();
    } catch {
      throw new CopilotKnowledgeFileError("identity");
    }
    if (
      !before ||
      typeof before.username !== "string" ||
      !before.username ||
      before.username.length > 320 ||
      !guid(before.tenantId) ||
      !guid(before.localAccountId) ||
      !auth.tryGetTokenForResourceSilent
    )
      throw new CopilotKnowledgeFileError("authentication");
    const owner = {
      username: before.username,
      tenantId: before.tenantId,
      localAccountId: before.localAccountId,
    };
    const controller = new AbortController();
    const end = Date.now() + DEADLINE_MS;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const check = () => {
      if (controller.signal.aborted || Date.now() >= end)
        throw new CopilotKnowledgeFileError("deadline");
      let current;
      try {
        current = auth.getCurrentAccount();
      } catch {
        throw new CopilotKnowledgeFileError("identity");
      }
      if (
        !current ||
        current.username !== owner.username ||
        current.tenantId !== owner.tenantId ||
        current.localAccountId !== owner.localAccountId
      )
        throw new CopilotKnowledgeFileError("identity");
    };
    type Status = {
      metadataCreate: "not-attempted" | "accepted" | "not-verified";
      binaryUpload: "not-attempted" | "accepted" | "not-verified";
      readback: "not-attempted" | "verified" | "not-verified";
    };
    const progress: Status = {
      metadataCreate: "not-attempted",
      binaryUpload: "not-attempted",
      readback: "not-attempted",
    };
    let createAttempted = false;
    let plan:
      | {
          plannedComponentId: string;
          expectedPlanSha256: string;
          agentId: string;
          filename: string;
          schemaName: string;
          contentSha256: string;
          contentBytes: number;
        }
      | undefined;
    const partial = (reason: string) => ({
      mode: "confirmed-storage-attempt",
      plan,
      ...progress,
      mayHaveCreatedComponent: createAttempted,
      componentId: plannedComponentId,
      failure: reason,
      ingestion: "unknown",
      notice: `A new component may remain without a verified file. Inspect this exact component ID; do not retry automatically. ${LIMITATION}`,
    });
    const work = async () => {
      let token: unknown;
      try {
        token = await auth.tryGetTokenForResourceSilent!(this.origin);
      } catch {
        throw new CopilotKnowledgeFileError("authentication");
      }
      check();
      if (typeof token !== "string" || !token || token.length > 65536 || /[\r\n]/.test(token))
        throw new CopilotKnowledgeFileError("authentication");
      const send = async (
        method: "GET" | "POST" | "PATCH",
        path: string,
        payload?: string | Buffer,
        extra: Record<string, string> = {},
        binary = false,
      ): Promise<Buffer> => {
        check();
        let response: Awaited<ReturnType<typeof request>> | undefined;
        try {
          response = await request(`${this.origin}/api/data/v9.2/${path}`, {
            method,
            headers: {
              Authorization: `Bearer ${token}`,
              "OData-Version": "4.0",
              "OData-MaxVersion": "4.0",
              Accept: binary ? "application/octet-stream" : "application/json",
              ...extra,
            },
            ...(payload === undefined ? {} : { body: payload }),
            signal: controller.signal,
            headersTimeout: Math.max(1, end - Date.now()),
            bodyTimeout: Math.max(1, end - Date.now()),
          });
          check();
          if (method !== "GET") {
            if (response.statusCode !== 204 && !(method === "POST" && response.statusCode === 201))
              throw new CopilotKnowledgeFileError("http", response.statusCode);
            if (method === "POST") {
              const created = response.headers["odata-entityid"];
              if (typeof created !== "string" || created.length > 500)
                throw new CopilotKnowledgeFileError("response");
              let url: URL;
              try {
                url = new URL(created);
              } catch {
                throw new CopilotKnowledgeFileError("response");
              }
              if (
                url.origin !== this.origin ||
                url.username ||
                url.password ||
                url.search ||
                url.hash ||
                url.pathname.toLowerCase() !== `/api/data/v9.2/botcomponents(${plannedComponentId})`
              )
                throw new CopilotKnowledgeFileError("response");
            }
            return Buffer.alloc(0); // Never inspect write/error response bodies.
          }
          if (response.statusCode !== 200)
            throw new CopilotKnowledgeFileError("http", response.statusCode);
          const chunks: Buffer[] = [];
          let size = 0;
          for await (const chunk of response.body) {
            check();
            const bytes: Buffer = Buffer.from(chunk as Uint8Array);
            size += bytes.length;
            if (size > MAX_BYTES) throw new CopilotKnowledgeFileError("response");
            chunks.push(bytes);
          }
          check();
          return Buffer.concat(chunks);
        } catch (error) {
          if (error instanceof CopilotKnowledgeFileError) throw error;
          throw new CopilotKnowledgeFileError("response");
        } finally {
          try {
            response?.body.destroy();
          } catch {
            /* Fixed outcomes survive cleanup. */
          }
        }
      };
      const json = async (path: string): Promise<Record<string, unknown>> => {
        const bytes = await send("GET", path);
        let value: unknown;
        try {
          value = JSON.parse(bytes.toString("utf8"));
        } catch {
          throw new CopilotKnowledgeFileError("response");
        }
        if (!object(value)) throw new CopilotKnowledgeFileError("response");
        return value;
      };
      const bot = await json(`bots(${agentId})?$select=botid,schemaname`);
      if (
        !guid(bot.botid) ||
        bot.botid.toLowerCase() !== agentId ||
        typeof bot.schemaname !== "string" ||
        !/^[A-Za-z_][A-Za-z0-9_]{0,99}$(?![\s\S])/.test(bot.schemaname)
      )
        throw new CopilotKnowledgeFileError("preflight");
      const schemaName = `${bot.schemaname}.file.McpText${plannedComponentId.replace(/-/g, "")}`;
      if (schemaName.length > 100) throw new CopilotKnowledgeFileError("preflight");
      const expectedPlanSha256 = sha(
        JSON.stringify({
          version: 1,
          origin: this.origin,
          agentId,
          plannedComponentId,
          schemaName,
          filename: args.filename,
          description: args.description,
          contentSha256,
          contentBytes: content.length,
        }),
      );
      plan = {
        plannedComponentId,
        expectedPlanSha256,
        agentId,
        filename: args.filename,
        schemaName,
        contentSha256,
        contentBytes: content.length,
      };
      if (args.confirm && args.expectedPlanSha256?.toLowerCase() !== expectedPlanSha256)
        throw new CopilotKnowledgeFileError("preflight");
      const filter = `_parentbotid_value eq ${agentId} and (name eq '${args.filename}' or schemaname eq '${schemaName}')`;
      const existing = await json(
        `botcomponents?$select=botcomponentid&$filter=${encodeURIComponent(filter)}&$top=2`,
      );
      if (
        !Array.isArray(existing.value) ||
        existing.value.length !== 0 ||
        existing["@odata.nextLink"] !== undefined
      )
        throw new CopilotKnowledgeFileError("preflight");
      if (!args.confirm)
        return {
          mode: "preview",
          plan,
          ...progress,
          ingestion: "unknown",
          notice: `Review this plan, then repeat the exact inputs with confirm=true and both returned plan fields. No write was sent. ${LIMITATION}`,
        };
      check();
      createAttempted = true;
      progress.metadataCreate = "not-verified";
      await send(
        "POST",
        "botcomponents",
        JSON.stringify({
          botcomponentid: plannedComponentId,
          componenttype: 14,
          name: args.filename,
          description: args.description,
          schemaname: schemaName,
          "parentbotid@odata.bind": `/bots(${agentId})`,
        }),
        { "Content-Type": "application/json", Prefer: "return=minimal" },
      );
      progress.metadataCreate = "accepted";
      const rowPath = `botcomponents(${plannedComponentId})?$select=${ROW_SELECT}`;
      const matches = (row: Record<string, unknown>) =>
        guid(row.botcomponentid) &&
        row.botcomponentid.toLowerCase() === plannedComponentId &&
        guid(row._parentbotid_value) &&
        row._parentbotid_value.toLowerCase() === agentId &&
        row.componenttype === 14 &&
        row.schemaname === schemaName &&
        row.name === args.filename &&
        (args.description === ""
          ? row.description === "" || row.description === null
          : row.description === args.description);
      const fresh = await json(rowPath);
      if (
        !matches(fresh) ||
        fresh.filedata !== null ||
        typeof fresh["@odata.etag"] !== "string" ||
        !/^W\/"[0-9]{1,20}"$(?![\s\S])/.test(fresh["@odata.etag"])
      )
        throw new CopilotKnowledgeFileError("preflight");
      progress.binaryUpload = "not-verified";
      await send("PATCH", `botcomponents(${plannedComponentId})/filedata`, content, {
        "Content-Type": "application/octet-stream",
        "x-ms-file-name": args.filename,
        "If-Match": fresh["@odata.etag"],
      });
      progress.binaryUpload = "accepted";
      progress.readback = "not-verified";
      const after = await json(rowPath);
      if (!matches(after) || !guid(after.filedata))
        throw new CopilotKnowledgeFileError("preflight");
      const stored = await send(
        "GET",
        `botcomponents(${plannedComponentId})/filedata/$value`,
        undefined,
        {},
        true,
      );
      if (stored.length !== content.length || sha(stored) !== contentSha256)
        throw new CopilotKnowledgeFileError("response");
      progress.readback = "verified";
      return {
        mode: "confirmed-storage-attempt",
        plan,
        ...progress,
        componentId: plannedComponentId,
        mayHaveCreatedComponent: true,
        ingestion: "unknown",
        notice: `New metadata and binary upload were accepted; exact returned metadata and binary SHA-256 matched at observation time. ${LIMITATION}`,
      };
    };
    try {
      return await Promise.race([
        work(),
        new Promise<never>((_, reject) => {
          timer = setTimeout(() => {
            controller.abort();
            reject(new CopilotKnowledgeFileError("deadline"));
          }, DEADLINE_MS);
        }),
      ]);
    } catch (error) {
      const reason = error instanceof CopilotKnowledgeFileError ? error.reason : "response";
      if (createAttempted) return partial(reason);
      if (error instanceof CopilotKnowledgeFileError) throw error;
      throw new CopilotKnowledgeFileError("response");
    } finally {
      clearTimeout(timer);
      controller.abort();
    }
  }
}
